import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { MoreHorizontal, X } from '../../icons';
import { Badge } from '../shared/Badge';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tLayout, getLayoutLang, subscribeLayoutLang } from '../../lib/layout-i18n';
import type { AllMenuEntry } from './AllMenuPanel';
import './RailMorePopover.css';

/**
 * 레일 «더보기» — 화면 높이에 안 들어가 접힌 메뉴만 담는다 (2026-08-26).
 *
 * ── 전체메뉴와 무엇이 다른가 ─────────────────────────────────────────────
 * 역할이 다르다. **전체메뉴는 «다 보여 준다», 더보기는 «이 레일에서 접힌 것만»** 이다.
 * 지금까지는 전체메뉴가 두 몫을 겸했고, 그래서 레일이 잘려 나간 것을 아무도 몰랐다 — 무엇이
 * 없어졌는지 알려 주는 자리가 없었기 때문이다. 여기는 개수를 배지로 달고 접힌 것만 세운다.
 *
 * 계약은 `AllMenuPopover` 와 같다(그쪽 머리말 참조) — body 로 portal + 앵커 실측 fixed 배치 ·
 * 스크림 없음 · 바깥 클릭/ESC 로 닫기 · `useModalLeave(160)` 진입·퇴장 모션. **그 파일은 한 줄도
 * 고치지 않았다** — 열림 상태를 `useAllMenuStore` 하나로 공유하면 전체메뉴와 서로를 닫아 버리고,
 * 목록 주입도 한쪽으로 덮인다. 그래서 열림 상태는 NavBar 의 지역 상태로 따로 둔다.
 *
 * 목록은 여기서도 **NavBar 가 계산한 것을 주입받는다**(자체 fetch 없음). 접기 판정이 레일에서
 * 일어나므로 그 결과를 그대로 받아야 둘이 갈라지지 않는다.
 */

/** 팝오버 폭·여백(px) — CSS `.railmore-pop` 과 동기 유지(AllMenuPopover 와 같은 값). */
const POP_WIDTH = 260;
const GAP = 10;
const EDGE = 12;

interface PopoverProps {
  /** 접힌 메뉴들. 비어 있으면 NavBar 가 이 컴포넌트를 아예 그리지 않는다. */
  items: AllMenuEntry[];
  open: boolean;
  onClose: () => void;
  /** 앵커(레일의 «더보기» 버튼). 팝오버는 이 요소의 오른쪽에 붙는다. */
  anchorRef: React.RefObject<HTMLElement>;
}

export function RailMorePopover({ items, open, onClose, anchorRef }: PopoverProps): JSX.Element | null {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const { shouldRender, isLeaving } = useModalLeave(open, 160);

  const close = useCallback(() => onClose(), [onClose]);
  useEscapeClose(close, !open);

  // 앵커 기준 위치 — 레일 오른쪽에 붙이고, 세로는 버튼 아래끝 정렬(= 버튼에서 솟는 모양).
  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const h = panelRef.current?.offsetHeight ?? 320;
    const left = Math.min(r.right + GAP, Math.max(EDGE, window.innerWidth - POP_WIDTH - EDGE));
    const top = Math.min(Math.max(EDGE, r.bottom - h), Math.max(EDGE, window.innerHeight - h - EDGE));
    setPos({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => { if (shouldRender) place(); }, [shouldRender, items, place]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  // 바깥 클릭 닫기 — 앵커는 제외한다(앵커 클릭은 토글이라 여기서 닫으면 곧바로 다시 열려 깜빡인다).
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      close();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', onPointer), 0);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', onPointer); };
  }, [open, close, anchorRef]);

  // 포커스 — 열 때 닫기 버튼으로, 닫을 때 직전 요소(=더보기 버튼)로 복원.
  useEffect(() => {
    if (open) {
      restoreRef.current = document.activeElement as HTMLElement | null;
      closeRef.current?.focus();
      return;
    }
    const prev = restoreRef.current;
    restoreRef.current = null;
    if (prev && document.contains(prev)) prev.focus();
  }, [open]);

  if (!shouldRender) return null;

  const title = tLayout('layout.railmore.title');
  const closeLabel = tLayout('layout.railmore.close');

  return createPortal(
    <div
      ref={panelRef}
      className={`railmore-pop${isLeaving ? ' railmore-pop--leaving' : ''}`}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      role="dialog"
      aria-label={title}
      aria-hidden={isLeaving || undefined}
    >
      <div className="railmore-pop__head">
        <b className="railmore-pop__title">{title}</b>
        <span className="railmore-pop__count">{tLayout('layout.allmenu.count', { n: items.length })}</span>
        <button
          type="button"
          ref={closeRef}
          className="railmore-pop__close"
          aria-label={closeLabel}
          title={closeLabel}
          onClick={close}
        >
          <X size={16} />
        </button>
      </div>

      <div className="railmore-pop__body scrollbar-thin">
        {items.map((item) => <RailMoreRow key={item.to} item={item} onPick={close} />)}
      </div>

      {/* 왜 여기 있는지 · 어떻게 빼는지 — 관리자가 메뉴를 끈 것이 아니라 «자리가 없어서» 다. */}
      <div className="railmore-pop__foot">
        {tLayout('layout.railmore.hint')}
        <br />
        {tLayout('layout.railmore.hint_pin')}
      </div>
    </div>,
    document.body,
  );
}

/** 목록 한 행 — 아이콘 칩 + 전체 라벨 + 배지. 전체메뉴 팝오버의 행과 같은 리듬. */
function RailMoreRow({ item, onPick }: { item: AllMenuEntry; onPick: () => void }) {
  const Icon = item.icon;
  const inner = (
    <>
      <span className="railmore-pop__icon" aria-hidden>
        {item.svg
          ? <span className="railmore-pop__svg" dangerouslySetInnerHTML={{ __html: item.svg }} />
          : <Icon size={16} />}
      </span>
      <span className="railmore-pop__label">{item.labelFull ?? item.label}</span>
      {item.badge !== undefined && item.badge > 0 && (
        <Badge variant="count" size="sm">{item.badge > 99 ? '99+' : item.badge}</Badge>
      )}
    </>
  );
  if (item.external) {
    return (
      <a className="railmore-pop__row" href={item.to} target="_blank" rel="noopener noreferrer" onClick={onPick}>
        {inner}
      </a>
    );
  }
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => `railmore-pop__row${isActive ? ' railmore-pop__row--active' : ''}`}
      onClick={onPick}
    >
      {inner}
    </NavLink>
  );
}

interface ItemProps {
  /** 접힌 개수 — 배지로 적는다. «무엇이 없어졌는지 모르는 상태»를 만들지 않기 위한 것이다. */
  count: number;
  open: boolean;
  onToggle: () => void;
  anchorRef: React.RefObject<HTMLButtonElement>;
}

/**
 * 레일의 «더보기» 칸 — `AllMenuNavItem` 과 같은 클래스를 써서 세로 배치 규칙을 그대로 탄다.
 * 넘칠 때만 만들어지는 칸이라, 다 들어가는 화면에서는 존재하지 않는다.
 */
export function RailMoreItem({ count, open, onToggle, anchorRef }: ItemProps): JSX.Element {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const label = tLayout('layout.nav.rail_more');
  return (
    <li>
      <button
        type="button"
        ref={anchorRef}
        className={['navbar-item', 'navbar-item-railmore', open && 'navbar-item-railmore-open'].filter(Boolean).join(' ')}
        aria-expanded={open}
        title={`${label} (${count})`}
        onClick={onToggle}
      >
        <MoreHorizontal size={18} />
        <span className="navbar-label">{label}</span>
        <span className="navbar-badge">
          <Badge variant="default" size="sm">{count > 99 ? '99+' : count}</Badge>
        </span>
      </button>
    </li>
  );
}
